/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import { EmptySubscription } from '@ahamove/icons/empty';
import type { DefaultPaymentResponse } from '@ahm/api-wrappers-core';
import { Box, Skeleton, Stack, Text, rem } from '@mantine/core';
import { openContextModal } from '@mantine/modals';
import { useTranslations } from 'next-intl';
import { Virtuoso } from 'react-virtuoso';
import SubscriptionHistoryItem from './subscription-history-item';
import { useGetTransactions } from '@/api/payment/get-transactions-fn';
import { ModalName } from '@/components/modals';
import { ResultBlock } from '@/components/result-block';
import { useGetCheckoutPaymentMethod } from '@/features/checkout/payment/use-get-checkout-payment-methods';
import { CheckoutType } from '@/lib/constants';
import useIsMobile from '@/lib/is-mobile/use-is-mobile';

export default function SubscriptionHistoryList() {
  const tSubscription = useTranslations('subscription');
  const isMobile = useIsMobile();

  const { data: paymentMethodList } = useGetCheckoutPaymentMethod(
    CheckoutType.Subscription as string
  );

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading } =
    useGetTransactions({
      type: CheckoutType.Subscription.toUpperCase(),
    });
  // Flatten all transactions from all pages
  const allTransactions = data?.pages.flatMap((page) => page.data) ?? [];
  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  };

  const showCheckoutModal = (transaction: DefaultPaymentResponse) => {
    transaction.status = 'success';
    openContextModal({
      modal: ModalName.CheckoutResult,
      id: ModalName.CheckoutResult,
      innerProps: {
        transaction: {
          ...transaction,
          transaction_time: transaction.time,
          data: {
            payment_provider_log_id: transaction?._id,
          },
        },
        paymentMethod: paymentMethodList?.find(
          (method) =>
            method.code === (transaction.method_details.method_code as string)
        ),
        requestBody: {
          title: transaction.note,
          amount: Math.abs(transaction.amount),
          payment_type:
            transaction?.type.toLowerCase() || CheckoutType.Subscription,
          subscription_id: transaction.item_id,
        },
        isAutoClose: false,
      },
      p: 0,
      w: rem(410),
      h: rem(522),
      styles: {
        body: {
          position: 'relative',
          padding: 0,
        },
      },
      fullScreen: isMobile,
      closeOnClickOutside: false,
      closeOnEscape: false,
      withCloseButton: false,
      centered: true,
    });
  };

  if (isLoading) {
    return (
      <Stack
        px={isMobile ? rem(16) : rem(24)}
        gap={isMobile ? rem(8) : rem(16)}
        py={rem(16)}
      >
        {new Array(5).fill(null).map((_, index) => (
          <Skeleton key={index} h={rem(80)} radius={rem(8)} />
        ))}
      </Stack>
    );
  }

  if (!allTransactions.length) {
    return (
      <ResultBlock
        className="justify-start p-[24px] md:p-0"
        icon={EmptySubscription}
        iconSize={160}
        description={
          <Text size={isMobile ? 'sm' : 'md'} c="neutral.6">
            {tSubscription('empty_list_description')}
          </Text>
        }
      />
    );
  }

  function LoadingSpinner() {
    return (
      <Stack align="center" py={rem(20)}>
        <Text size="sm" c="neutral.6">
          Đang tải...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack
      w="100%"
      bg="white"
      gap={4}
      style={{
        borderRadius: rem(4),
      }}
    >
      {isLoading ? (
        <LoadingSpinner />
      ) : (
        <Virtuoso
          useWindowScroll
          style={{ height: '100%' }}
          data={allTransactions}
          endReached={loadMore}
          increaseViewportBy={200}
          itemContent={(index, transaction) => (
            <Box
              onClick={() => showCheckoutModal(transaction)}
              px={isMobile ? rem(16) : rem(24)}
              py={rem(4)}
            >
              <SubscriptionHistoryItem
                key={transaction._id}
                data={transaction}
              />
            </Box>
          )}
          components={{
            Footer: () => {
              return isFetchingNextPage ? <LoadingSpinner /> : null;
            },
          }}
        />
      )}
    </Stack>
  );
}
